import {
  WppButton,
  WppIconAddCircle,
  WppIconRemoveCircle,
  WppLabel,
  WppTextareaInput,
  WppTypography,
} from '@wppopen/components-library-react'
import clsx from 'clsx'
import { TextareaInputChangeEventDetail } from '@wppopen/components-library'
import { WppTextareaInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import {
  StateToTypeMap,
  FormReducerAction,
  StateToListFieldMap,
  formHelperMethodFactory,
} from 'reducers/FormReducer/FormReducer'
import { NoneAddedRender } from '../NoneAddedRender/NoneAddedRender'
import { FormReducerListField } from 'reducers/FormReducer/utils'
import styles from './FormList.module.scss'

export interface FormListProps<T extends keyof StateToTypeMap> {
  disabled: boolean
  id: StateToListFieldMap<T>
  label: string
  disclaimer?: string
  state: StateToTypeMap[T]
  reducerDispatch: React.Dispatch<FormReducerAction>
  placeholder?: string
  className?: string
  // extend the base form helper functions for nested dispatch actions
  nestedDispatchWrapper?: (wrappedAction: FormReducerAction, ...rest: any[]) => any
  nestedIdx?: number | null
  tooltip?: string
}

function FormList<T extends keyof StateToTypeMap>({
  disabled,
  id,
  label,
  disclaimer,
  state,
  reducerDispatch,
  placeholder,
  className,
  nestedIdx = null,
  nestedDispatchWrapper,
  tooltip,
}: FormListProps<T>) {
  //  get the value of the field from the reducer state
  const listItem = state[id as keyof typeof state] as FormReducerListField

  const { addListItem, removeListItem, replaceListItem } = formHelperMethodFactory<T>()

  const handleAddListItem = () => {
    if (!nestedDispatchWrapper) {
      reducerDispatch(addListItem(id))
    } else {
      reducerDispatch(nestedDispatchWrapper(addListItem(id), nestedIdx))
    }
  }

  const handleRemoveListItem = (idx: number) => {
    if (!nestedDispatchWrapper) reducerDispatch(removeListItem(id, idx))
    else reducerDispatch(nestedDispatchWrapper(removeListItem(id, idx), nestedIdx))
  }

  const handleListItemChange = (
    e: WppTextareaInputCustomEvent<TextareaInputChangeEventDetail>,
    idx: number,
    id: StateToListFieldMap<T>,
  ) => {
    const { value } = e.target
    if (!nestedDispatchWrapper) reducerDispatch(replaceListItem(id, idx, value))
    else reducerDispatch(nestedDispatchWrapper(replaceListItem(id, idx, value), nestedIdx))
  }

  return (
    <div className={styles.fields_group__addInput} id={id as string} key={id as string}>
      <WppLabel
        htmlFor={id as string}
        typography="s-strong"
        config={{
          text: label,
          description: tooltip,
          icon: tooltip ? 'wpp-icon-info' : undefined,
        }}
        disabled={disabled}
        optional
      />
      {disclaimer && disabled && (
        <WppTypography type="xs-midi" tag="p">
          {disclaimer}
        </WppTypography>
      )}
      {/* input for guideline */}
      {disabled && !listItem.value.length ? (
        <NoneAddedRender />
      ) : (
        <ul>
          {listItem?.value?.map((item, index) => (
            // the key needs to be index otherwise the autofocus will not work
            <li key={index} className={clsx(styles.formListItem, className, { [styles.disabled]: disabled })}>
              {!disabled && (
                <WppIconRemoveCircle onClick={() => handleRemoveListItem(index)} data-testid="edit-list-item--remove" />
              )}
              <WppTextareaInput
                value={item}
                onWppChange={e => handleListItemChange(e, index, id)}
                placeholder={placeholder ?? `Enter ${label.toLocaleLowerCase()}`}
                data-testid="edit-list-item--textarea"
                rows={1}
                role="textbox"
                disabled={disabled}
              />
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <WppButton variant="secondary" data-testid="add-list-item-cta" onClick={handleAddListItem}>
          <WppIconAddCircle slot="icon-start" />
          Add
        </WppButton>
      )}
    </div>
  )
}

export default FormList
